import styled from "styled-components";
import { read, utils } from "xlsx";
import { BasicTable, DocAddIcon, InputDragDrop, StatePill, Text } from "@ficatranquilo/contabilidade.com-ui";
import { DomainStatus, escapeHtml } from "../ow";

// Importação por planilha (planilha → prévia → resultado), no que a importação de
// colaboradores (/c/employees) e a de valores variáveis (/c/contracts) compartilham.

const SHEET_ACCEPT = {
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "application/vnd.ms-excel": [".xls"],
};

/** Linhas da primeira aba da planilha, com os cabeçalhos como chaves (`sheet_to_json`). */
export function readSheetFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const workbook = read(event.target.result, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        resolve(utils.sheet_to_json(worksheet));
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

const DropIcon = styled(DocAddIcon)`
  width: 40px;
  height: 40px;
`;

/** Área para soltar a planilha (.xlsx ou .xls); `onFile` recebe o arquivo escolhido. */
export function SheetDropZone({ onFile }) {
  return (
    <InputDragDrop
      accept={SHEET_ACCEPT}
      $icon={<DropIcon />}
      $message="Arraste a planilha ou <strong>clique para escolher</strong>"
      onDrop={(accepted) => {
        if (accepted[0]) onFile(accepted[0]);
      }}
    />
  );
}

/**
 * Prévia e resultado em tabela longa: a rolagem fica dentro da caixa. O `Modal` da
 * lib não tem `max-height`, então a caixa é baixa o bastante para o modal `large`
 * caber num notebook (~650px úteis).
 */
export const TableScroll = styled.div`
  max-height: 260px;
  overflow: auto;
  border: 1px solid ${({ theme }) => theme.colors.N100};
  border-radius: 8px;

  /* Uma linha por célula: com quebra, cada linha da planilha ocupava quase a caixa toda */
  th,
  td {
    white-space: nowrap;
  }
`;

const StatusStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 200px;
  white-space: normal;
`;

/** Status de uma linha da prévia: "OK" ou "Com erro" com os erros embaixo. */
export function PreviewStatusCell({ errors }) {
  if (!errors?.length) return <StatePill $text="OK" $variant="green" $size="small" />;
  return (
    <StatusStack>
      <StatePill $text="Com erro" $variant="red" $size="small" />
      <Text $text={escapeHtml(errors.join("; "))} $variant="caption_medium" $color="E400" />
    </StatusStack>
  );
}

const plural = (count, one, many) => `<strong>${count}</strong> ${count === 1 ? one : many}`;

/**
 * Resumo em uma linha, separado por "·". Cada item: `{ count, one, many }`
 * ("1 linha", "12 linhas"); itens com `hideZero` e contagem zero saem.
 */
export function ImportCounts({ items }) {
  const text = items
    .filter((item) => !(item.hideZero && !item.count))
    .map((item) => plural(item.count || 0, item.one, item.many))
    .join(" · ");
  return <Text $text={text} $variant="paragraph_small_medium" $color="N700" $colorEmphasis="N900" />;
}

/** Status que o back devolve em cada linha do resultado → valor do `DomainStatus importacao`. */
const IMPORT_STATUS = { imported: "importado", linked: "vinculado", skipped: "ignorado", failed: "falhou" };

/**
 * Resultado da importação (`details` do back: `{ row, status, reason, ... }`).
 * `columns` são as colunas do meio, entre "Linha" e "Status".
 */
export function ImportResultTable({ details, columns = [] }) {
  const allColumns = [
    { id: "row", header: "Linha", size: 70, cell: ({ row }) => row.original.row ?? "-" },
    ...columns,
    {
      id: "status",
      header: "Status",
      size: 120,
      cell: ({ row }) => <DomainStatus domain="importacao" value={IMPORT_STATUS[row.original.status] || row.original.status} />,
    },
    { id: "reason", header: "Motivo", size: 260, cell: ({ row }) => row.original.reason || "-" },
  ];

  return (
    <TableScroll>
      <BasicTable data={details} columns={allColumns} getRowId={(_, index) => String(index)} />
    </TableScroll>
  );
}
